/**
 * Demo page showcasing walkthrough viewer for IBS 2023
 * @module
 *
 */
import { createFileRoute } from "@tanstack/react-router";
import { useSearch } from "@tanstack/react-router";
import type { Walkthrough } from "gfx/walkthrough";
import { startingWalkthrough } from "gfx/walkthrough";
import type { OptionPropsWithImage } from "lib/components/demo/Ibs2023LandingPage";
import Ibs2023LandingPage from "lib/components/demo/Ibs2023LandingPage";
import ShowroomPhotorealDemo from "lib/components/showroom2/pages/ShowroomPhotorealDemo";
import { ShowroomProvider } from "lib/components/showroom2/state/useShowroomState";
import getPageTitle from "lib/utils/getPageTitle";
import { useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import { useAppNavigate } from "ui/hooks/useAppNavigate";

const WALKTHROUGH_OPTIONS: OptionPropsWithImage[] = [
  {
    label: "Tour the Orion",
    value: "orion",
    imageUrl: "https://photoreal-test.s3.amazonaws.com/demos/ibs2023-walkthrough-orion.png",
  },
];

function InteriorWalkthroughPage() {
  const query = useSearch({ strict: false }) as Record<string, string>;
  const navigate = useAppNavigate();

  const [walkthrough, setWalkthrough] = useState<Walkthrough | undefined>(startingWalkthrough);

  if (!query.scene) {
    return (
      <>
        <Head>
          <title>{getPageTitle("Higharc Photoreal - Walkthrough")}</title>
        </Head>

        <Ibs2023LandingPage
          headline="Introducing Photoreal Tours"
          secondaryMessage="Gorgeous, painless, and impossibly accurate. Created directly from your plans."
          options={WALKTHROUGH_OPTIONS}
          onSelect={(value) => navigate({ search: { scene: value } as never })}
        />
      </>
    );
  }

  return (
    <ShowroomProvider key={query.scene}>
      <ShowroomPhotorealDemo walkthrough={walkthrough} setWalkthrough={setWalkthrough} back />
    </ShowroomProvider>
  );
}

export const Route = createFileRoute("/demo/photoreal-walkthrough")({
  component: InteriorWalkthroughPage,
  staticData: { isPublicPage: true },
});
